import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  LoginRequest,
  LoginResponse,
  Modulo,
  RefreshResponse,
  UsuarioProfile,
} from '../models/auth.models';

const ACCESS_TOKEN_KEY = 'tms_access_token';
const REFRESH_TOKEN_KEY = 'tms_refresh_token';
const USER_KEY = 'tms_user';
const PERMISOS_KEY = 'tms_permisos';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _currentUser = signal<UsuarioProfile | null>(
    this.loadFromStorage<UsuarioProfile>(USER_KEY),
  );
  private readonly _permisos = signal<Modulo[]>(this.loadFromStorage<Modulo[]>(PERMISOS_KEY) ?? []);
  private readonly _isLoading = signal(false);

  readonly currentUser = this._currentUser.asReadonly();
  readonly permisos = this._permisos.asReadonly();
  readonly isAuthenticated = computed(() => !!this._currentUser());
  readonly isLoading = this._isLoading.asReadonly();

  login(credentials: LoginRequest): Observable<LoginResponse> {
    this._isLoading.set(true);

    const payload = {
      usu_acceso: credentials.usu_acceso,
      password: credentials.password,
      sis_id: 1,
    };

    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, payload).pipe(
      tap((response) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
        localStorage.setItem(USER_KEY, JSON.stringify(response.user));
        localStorage.setItem(PERMISOS_KEY, JSON.stringify(response.permisos));
        this._currentUser.set(response.user);
        this._permisos.set(response.permisos ?? []);
        this._isLoading.set(false);
      }),
      catchError((err) => {
        this._isLoading.set(false);
        return throwError(() => err);
      }),
    );
  }

  tieneServicio(idService: number): boolean {
    return this._permisos().some((modulo) =>
      modulo.menu.some((item) => item.servicios?.some((s) => s.id_service === idService)),
    );
  }

  /**
   * ¿Tiene un servicio cuyo nombre empieza con el código? (p. ej. `PLN_PLAN_VER - Ver planificación`).
   * Se usa cuando el id del servicio cambia de un ambiente a otro y el código es lo estable.
   */
  tieneServicioPorCodigo(codigo: string): boolean {
    return this._permisos().some((modulo) =>
      modulo.menu.some((item) =>
        item.servicios?.some((s) => (s.serv_nombre ?? '').trim().startsWith(`${codigo} -`) || s.serv_nombre?.trim() === codigo),
      ),
    );
  }

  readonly canLogout = computed(() => {
    const user = this._currentUser();
    // Si el usuario no está autenticado, no puede logout (no debería pasar)
    if (!user) return false;
    // Solo permite logout si NO es el cargo de auditor (cargo_id === 76)
    return user.cargo_id !== 76;
  });

  refreshToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<RefreshResponse>(`${environment.apiUrl}/auth/refresh`, { refreshToken })
      .pipe(
        tap((response) => {
          localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
        }),
        map((response) => response.accessToken),
      );
  }

  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMISOS_KEY);
    this._currentUser.set(null);
    this._permisos.set([]);
    this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  changePassword(passwordActual: string, passwordNueva: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/change-password`, {
      passwordActual,
      passwordNueva,
    });
  }

  private loadFromStorage<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }
}
